/**
 * haptisk — taktil tilbakemelding på tvers av plattformer (runde 44).
 *
 * To distinkte miljøer:
 *  1. Native (Capacitor iOS/Android wrapper): bruker `@capacitor/haptics`
 *     for ekte vibrasjons-motor. Modulen lastes dynamisk slik at web-
 *     builden ikke trekker den med i bundle hvis vi ikke kjører native.
 *  2. Web (PWA i Safari/Chrome på mobil): bruker `navigator.vibrate` som
 *     fallback. Ikke alle nettlesere/operativsystem støtter dette
 *     (iOS Safari blokkerer det fortsatt) — vi feiler stille.
 *
 * I jsdom og serverkontekst er alle metoder no-ops.
 *
 * Bruk:
 *   import { haptisk } from '@/systems/haptisk';
 *   haptisk.lett();      // FAB-tap, mikro-interaksjon
 *   haptisk.middels();   // knapp-aksept (kontrahere, oppgradere)
 *   haptisk.tung();      // viktig hendelse (Brage-vinner)
 *   haptisk.suksess();   // suksess-mønster (tier-up)
 *   haptisk.feil();      // feil-mønster (avslag, ikke råd)
 */

type CapWindow = Window & {
  Capacitor?: {
    isNativePlatform?: () => boolean;
  };
};

type ImpactStyle = 'LIGHT' | 'MEDIUM' | 'HEAVY';
type NotificationType = 'SUCCESS' | 'WARNING' | 'ERROR';

interface HapticsModul {
  impact?: (opts: { style: ImpactStyle }) => Promise<void>;
  notification?: (opts: { type: NotificationType }) => Promise<void>;
  vibrate?: (opts: { duration: number }) => Promise<void>;
}

/** Cache for den lazy-importerte Capacitor-modulen. */
let hapticsModulPromise: Promise<HapticsModul | null> | null = null;

function erNative(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as CapWindow;
  return !!w.Capacitor?.isNativePlatform?.();
}

async function hentNativeHaptics(): Promise<HapticsModul | null> {
  if (!erNative()) return null;
  if (!hapticsModulPromise) {
    hapticsModulPromise = (async () => {
      try {
        // Dynamic import slik at web-bundle ikke krever modulen.
        // Vite vil ikke pre-bundle dette siden navnet er literalt
        // og modulen er valgfri i package.json. Vi caster modulnavnet
        // til en variabel slik at tsc ikke krever type-decl ved web-bygg.
        const modName = '@capacitor/haptics';
        const mod = (await import(/* @vite-ignore */ modName)) as {
          Haptics?: HapticsModul;
        };
        return mod.Haptics ?? null;
      } catch {
        return null;
      }
    })();
  }
  return hapticsModulPromise;
}

function webVibrate(mønster: number | number[]): boolean {
  if (typeof navigator === 'undefined') return false;
  // Vibration API tar både `number` og `number[]`. lib.dom har en
  // smalere `Iterable<number>`-signatur som ikke fanger primitivt
  // tall, så vi caster til en eksplisitt union.
  const vibrate = (navigator as unknown as {
    vibrate?: (m: number | number[]) => boolean;
  }).vibrate;
  if (typeof vibrate !== 'function') return false;
  try {
    return vibrate.call(navigator, mønster);
  } catch {
    return false;
  }
}

async function impact(style: ImpactStyle, webMs: number): Promise<void> {
  const native = await hentNativeHaptics();
  if (native?.impact) {
    try {
      await native.impact({ style });
      return;
    } catch {
      // fall gjennom til web-fallback
    }
  }
  webVibrate(webMs);
}

async function notification(type: NotificationType, webMønster: number[]): Promise<void> {
  const native = await hentNativeHaptics();
  if (native?.notification) {
    try {
      await native.notification({ type });
      return;
    } catch {
      // fall gjennom
    }
  }
  webVibrate(webMønster);
}

export const haptisk = {
  /** Lett tap — mikro-interaksjoner, FAB-trykk. */
  lett(): void {
    void impact('LIGHT', 10);
  },
  /** Middels tap — knapp-aksept (kontrahere, oppgradere). */
  middels(): void {
    void impact('MEDIUM', 25);
  },
  /** Tung tap — viktige hendelser (Brage-vinner, store milestones). */
  tung(): void {
    void impact('HEAVY', 50);
  },
  /** Suksess-mønster — tier-opprykk, ferdig kampanje, prisvinner. */
  suksess(): void {
    void notification('SUCCESS', [25, 60, 25]);
  },
  /** Feil-mønster — avslag, ikke nok kroner, ugyldig handling. */
  feil(): void {
    void notification('ERROR', [40, 40, 40, 40, 80]);
  },
  /**
   * Test-helper: rapporter om vi anser miljøet som native (Capacitor).
   * Brukes av tester for å verifisere fall-through til web-fallback.
   */
  _erNative: erNative,
  /**
   * Test-helper: nullstill cache av Capacitor-modulen.
   */
  _reset(): void {
    hapticsModulPromise = null;
  },
};

export type Haptisk = typeof haptisk;
